/**
 * GET/PUT /downhole/settings (api#1052): the company's Directional & MWD
 * switch, `dd_mwd_enabled`.
 *
 * `useDownholeFeature` follows `useTruckingFeature`'s shape (one boolean per
 * feature, one shared query key) with the OPPOSITE loading default. Trucking
 * reads loading as ON so existing nav items do not flash out. Every screen this
 * switch will gate is new and additive, so a screen that appears before the
 * answer arrives would offer DD/MWD work to a company that has it off. So
 * loading, a failed read, a refusal and a tenant without downhole_tools all
 * read as OFF. Only a delivered `true` turns it on.
 *
 * The query only runs for a downhole_tools tenant. The API answers 403 to every
 * other tenant (requireVertical on the whole namespace), and asking would put a
 * refusal in the log on every page that checks the switch.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { unwrapDownhole } from './useDownhole';
import type { DhSettings, DhSettingsWrite, DownholeEnvelope } from '../types/downhole';

export const DOWNHOLE_SETTINGS_KEY = ['settings', 'downhole'] as const;

export function useDownholeSettings() {
  const { hasVertical } = useAuth();
  return useQuery({
    queryKey: DOWNHOLE_SETTINGS_KEY,
    queryFn: async () => unwrapDownhole<DhSettings>((await apiClient.get<DownholeEnvelope<DhSettings>>('/downhole/settings')).data),
    enabled: hasVertical('downhole_tools'),
    staleTime: 60_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

const FEATURE_FIELD = { dd_mwd: 'dd_mwd_enabled' } as const satisfies Record<string, keyof DhSettings>;

export function useDownholeFeature(key: keyof typeof FEATURE_FIELD): boolean {
  const { data } = useDownholeSettings();
  // `=== true`, not `!== false`: anything short of a delivered true is off.
  return data?.data?.[FEATURE_FIELD[key]] === true;
}

/** PUT is non-strict requireRole('admin'); gate the control with useCanWriteCompanySettings. */
export function useUpdateDownholeSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: DhSettingsWrite) =>
      unwrapDownhole<DhSettings>((await apiClient.put<DownholeEnvelope<DhSettings>>('/downhole/settings', body)).data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DOWNHOLE_SETTINGS_KEY }),
  });
}
